<template>
  <Modal v-if="isAdmin" dismissible>
    <template #activator="{ show }">
      <button
        class="ml-2 px-2 py-2"
        :class="[isShown ? 'text-white/80 hover:text-white' : '']"
        @click.stop="show"
      >
        <UsersIcon class="w-5 h-5" />
      </button>
    </template>
    <template #content>
      <div class="rounded-lg w-[900px] h-[600px]">
        <div class="h-full flex flex-col">
          <div class="h-full flex flex-col overflow-hidden">
            <div
              class="flex p-6 justify-between items-center border-b border-slate-300"
            >
              <h2
                class="text-primary text-lg font-semibold capitalize> text-center flex gap-2 items-center"
              >
                Assign members to project
              </h2>
            </div>
            <div class="flex px-8 flex-col gap-6 w-full sticky top-0 z-50">
              <div class="bg-white z-50 space-y-6 py-4">
                <div class="space-y-4">
                  <ProjectMembers
                    :project="project"
                    class="shrink-0 w-[400px]"
                  />

                  <div class="w-full">
                    <div class="w-full flex flex-wrap gap-4">
                      <template
                        v-for="user in project.users"
                        :key="'user-' + user.id"
                      >
                        <div
                          class="rounded-full dark:text-white text-black text-center max-w-fit border-2 px-3 py-2 flex gap-1.5 items-center whitespace-nowrap hover:cursor-pointer"
                          :style="{
                            'background-color': `${usersColors[user?.id ?? '']}40`,
                          }"
                        >
                          <div class="leading-none">
                            {{ buildUserFullname(user) }}
                          </div>
                          <div class="w-5 h-5 flex items-center justify-center">
                            <button
                              v-tooltip="
                                !canDeleteMember()
                                  ? 'Cannot remove member. Assigned in some reviews.'
                                  : ''
                              "
                              :class="{
                                'opacity-50 cursor-not-allowed':
                                  !canDeleteMember(),
                              }"
                              class="hover:bg-transparent"
                              @click.stop="
                                removeProjectMember(project.id!, user?.id)
                              "
                            >
                              <XCircleIcon class="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> </template
  ></Modal>
</template>
<script setup lang="ts">
import { Project } from '@core/domain/models/project.model'
import { User } from '@core/domain/models/user.model'
import { computed } from 'vue'
import ProjectMembers from './ProjectMembers/ProjectMembers.vue'
import useProjects from '@app/views/Projects/use-projects'
import { injectStrict } from '@app/utils/injectStrict'
import { generateColorFromString } from '@app/utils/generateColorFromString'
import { AuthKey } from '@app/injectionKeys'
import { Role } from '@core/domain/types/role.type'
import Modal from '@app/components/Global/Modal/Modal.vue'
import { UsersIcon } from '@heroicons/vue/24/solid'
import { XCircleIcon } from '@heroicons/vue/24/outline'

const { user: authenticatedUser } = injectStrict(AuthKey)
const isAdmin = computed(() => authenticatedUser.value?.role === Role.ADMIN)

const props = defineProps<{
  project: Project
  isShown?: boolean
}>()

const { removeProjectMember } = useProjects()

const usersColors = computed<{ [userId: string]: string }>(
  () =>
    props.project.users?.reduce(
      (a, u) => ({
        ...a,
        [u.id]: generateColorFromString(`${u.firstName} ${u.lastName}`),
      }),
      {},
    ) ?? {},
)

const canDeleteMember = () => {
  return true
}

function buildUserFullname(user: User) {
  if (!user) return ''
  let fullName = ''
  if (user.firstName) {
    fullName += user.firstName
  }
  if (user.lastName) {
    fullName += ' ' + user.lastName
  }
  if (!fullName) {
    fullName = user.email
  }
  return fullName
}
</script>
